import type { ImportedBodyEntry } from './glb'
import { asRecord } from './utils'
import {
  areImportedBodyBindingIdsEquivalent,
  resolveStableImportedBodyBindingId,
} from '../utils'

function readStringCandidate(value: unknown): string {
  if (typeof value === 'string') {
    return value.trim()
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value)
  }
  return ''
}

/**
 * 判断字符串是否为内部技术实体名。
 *
 * @param value 待判断的实体显示名。
 * @returns 是否只能作为绑定身份而不能作为用户可见名称。
 */
export function isTechnicalImportedBodyDisplayName(value: unknown): boolean {
  const label = readStringCandidate(value)
  return label.length === 0
    || label.startsWith('mesh:')
    || label.includes('::')
}

function resolveBodyLabel(record: Record<string, unknown>, fallbackIndex: number): string {
  const label = [
    record.name,
    record.label,
    record.bodyName,
    record.displayName,
    record.entityName,
  ]
    .map((value) => readStringCandidate(value))
    .find((value) => value.length > 0 && !isTechnicalImportedBodyDisplayName(value))
  return label || `实体(${fallbackIndex})`
}

function resolveBodyId(record: Record<string, unknown>, label: string, fallbackIndex: number): string {
  const bodyId = [
    record.bodyId,
    record.entityId,
    record.id,
    record.uuid,
    record.name,
  ]
    .map((value) => readStringCandidate(value))
    .find((value) => value.length > 0)
  return bodyId || label || `body-${fallbackIndex}`
}

function normalizeImportedBodyEntry(rawEntry: unknown, fallbackIndex: number): ImportedBodyEntry | null {
  const rawLabel = readStringCandidate(rawEntry)
  if (rawLabel) {
    return {
      bodyId: rawLabel,
      label: isTechnicalImportedBodyDisplayName(rawLabel) ? `实体(${fallbackIndex})` : rawLabel,
    }
  }

  const record = asRecord(rawEntry)
  if (!record) {
    return null
  }

  const label = resolveBodyLabel(record, fallbackIndex)
  const bodyBindingId = [
    record.bodyBindingId,
    record.body_binding_id,
    record.bindingId,
    record.binding_id,
  ]
    .map((value) => readStringCandidate(value))
    .find((value) => value.length > 0)
  const geometryObjectId = [
    record.geometryObjectId,
    record.geometry_object_id,
  ]
    .map((value) => readStringCandidate(value))
    .find((value) => value.length > 0)
  return {
    bodyId: resolveBodyId(record, label, fallbackIndex),
    ...(bodyBindingId ? { bodyBindingId } : {}),
    ...(geometryObjectId ? { geometryObjectId } : {}),
    label,
  }
}

/**
 * 创建由绑定身份兜底出来的实体条目。
 *
 * 设计说明：
 * 离线包重载时局部高亮表、bodyBindingId 与 topology asset 需要保留完整技术 ID，
 * 但这些 ID 不能泄漏到设计树显示名。所有缺少可读名称的消费方都应走这个入口。
 *
 * @param input 兜底实体输入。
 * @returns 可用于 metadata/hierarchy 的实体条目。
 */
export function createFallbackImportedBodyEntry(input: {
  /** body 语义身份。 */
  readonly bodyId: unknown
  /** 精确 bodyBindingId；缺失时仅保留 bodyId。 */
  readonly bodyBindingId?: unknown
  /** Worker GeometryState 中的权威几何对象 ID。 */
  readonly geometryObjectId?: unknown
  /** 从 1 开始的实体序号。 */
  readonly fallbackIndex: number
  /** 可选显示名；为技术 ID 时会被丢弃。 */
  readonly fallbackLabel?: unknown
}): ImportedBodyEntry {
  const fallbackIndex = Number.isInteger(input.fallbackIndex) && input.fallbackIndex > 0
    ? input.fallbackIndex
    : 1
  const bodyId = readStringCandidate(input.bodyId) || `body-${fallbackIndex}`
  const bodyBindingId = readStringCandidate(input.bodyBindingId)
  const geometryObjectId = readStringCandidate(input.geometryObjectId)
  const fallbackLabel = readStringCandidate(input.fallbackLabel)
  const label = fallbackLabel && !isTechnicalImportedBodyDisplayName(fallbackLabel)
    ? fallbackLabel
    : `实体(${fallbackIndex})`

  return {
    bodyId,
    ...(bodyBindingId ? { bodyBindingId } : {}),
    ...(geometryObjectId ? { geometryObjectId } : {}),
    label,
  }
}

/**
 * 规范化实体条目的用户可见显示名。
 *
 * @param entry 原始实体条目。
 * @param fallbackIndex 从 1 开始的兜底序号。
 * @returns 显示名已清洗的实体条目。
 */
export function normalizeImportedBodyEntryDisplay(
  entry: ImportedBodyEntry,
  fallbackIndex: number,
): ImportedBodyEntry {
  if (!isTechnicalImportedBodyDisplayName(entry.label) && entry.label.trim() !== entry.bodyId.trim()) {
    return entry
  }

  return {
    ...entry,
    label: `实体(${fallbackIndex})`,
  }
}

export function parseImportedBodyEntries(rawEntries: unknown): ImportedBodyEntry[] {
  if (!Array.isArray(rawEntries)) {
    return []
  }

  return rawEntries.flatMap((entry, index) => {
    const normalized = normalizeImportedBodyEntry(entry, index + 1)
    return normalized ? [normalized] : []
  })
}

export function mergeBodyEntriesById(
  ...collections: Array<ImportedBodyEntry[] | undefined>
): ImportedBodyEntry[] {
  const mergedEntries: ImportedBodyEntry[] = []
  collections.forEach((entries) => {
    if (!Array.isArray(entries)) {
      return
    }
    entries.forEach((entry) => {
      const bodyId = entry.bodyId.trim()
      if (!bodyId) {
        return
      }
      const currentIndex = mergedEntries.findIndex((currentEntry) => (
        areImportedBodyEntriesEquivalent(currentEntry, entry)
      ))
      if (currentIndex < 0) {
        mergedEntries.push(entry)
        return
      }

      mergedEntries[currentIndex] = mergeEquivalentBodyEntry(mergedEntries[currentIndex]!, entry)
    })
  })
  return mergedEntries
}

/**
 * 判断实体名称是否为运行时生成的技术绑定 ID。
 *
 * @param entry 实体条目。
 * @returns 是否为运行时技术名称。
 */
export function isRuntimeImportedBodyEntryLabel(entry: ImportedBodyEntry): boolean {
  return isTechnicalImportedBodyDisplayName(entry.label)
}

/**
 * 判断实体名称是否只是技术兜底 ID。
 *
 * @param entry 实体条目。
 * @returns 是否为技术兜底名称。
 */
function isTechnicalBodyEntryLabel(entry: ImportedBodyEntry): boolean {
  const label = entry.label.trim()
  const bodyId = entry.bodyId.trim()
  return label.length === 0
    || label === bodyId
    || isRuntimeImportedBodyEntryLabel(entry)
}

/**
 * 读取实体条目的全部可匹配身份。
 *
 * @param entry 实体条目。
 * @returns 去重后的实体身份。
 */
function createBodyEntryIdentityCandidates(entry: ImportedBodyEntry): string[] {
  return Array.from(new Set([
    entry.bodyId,
    entry.bodyBindingId,
  ]
    .map((value) => (typeof value === 'string' ? value.trim() : ''))
    .filter((value) => value.length > 0)))
}

/**
 * 判断两个实体条目是否指向同一个 imported body。
 *
 * 设计说明：部分旧包会把可读条目的 `bodyId` 写成零件节点名，但 `bodyBindingId`
 * 仍然保留真实 mesh 绑定；如果只比较 bodyId，会在保存后生成“可读实体 + mesh 实体”两条。
 *
 * @param left 左侧实体条目。
 * @param right 右侧实体条目。
 * @returns 是否指向同一个 imported body。
 */
function areImportedBodyEntriesEquivalent(
  left: ImportedBodyEntry,
  right: ImportedBodyEntry,
): boolean {
  return createBodyEntryIdentityCandidates(left).some((leftId) => (
    createBodyEntryIdentityCandidates(right).some((rightId) => (
      areImportedBodyBindingIdsEquivalent(leftId, rightId)
    ))
  ))
}

/**
 * 计算实体 ID 的语义优先级。
 *
 * @param bodyId 实体 ID。
 * @returns 越大越适合保留为设计树语义 ID。
 */
function resolveBodyEntryIdScore(bodyId: string): number {
  const normalizedBodyId = bodyId.trim()
  if (!normalizedBodyId || normalizedBodyId.startsWith('mesh:') || normalizedBodyId.includes('::')) {
    return 0
  }
  return resolveStableImportedBodyBindingId(normalizedBodyId) === normalizedBodyId ? 2 : 1
}

/**
 * 合并两个指向同一 imported body 的实体条目。
 *
 * 设计说明：调用顺序仍表达来源优先级，但当后续条目携带更稳定的 bodyId 或更可读的 label 时，
 * 需要吸收这些语义，避免 `mesh:...` 这类绑定 ID 泄漏到设计树。
 *
 * @param current 已合并的条目。
 * @param candidate 新候选条目。
 * @returns 合并后的实体条目。
 */
function mergeEquivalentBodyEntry(
  current: ImportedBodyEntry,
  candidate: ImportedBodyEntry,
): ImportedBodyEntry {
  const preferredIdentity = resolveBodyEntryIdScore(candidate.bodyId) > resolveBodyEntryIdScore(current.bodyId)
    ? candidate
    : current
  const preferredLabel = isTechnicalBodyEntryLabel(current) && !isTechnicalBodyEntryLabel(candidate)
    ? candidate.label
    : current.label
  return {
    ...preferredIdentity,
    label: preferredLabel,
    geometryObjectId: preferredIdentity.geometryObjectId
      ?? current.geometryObjectId
      ?? candidate.geometryObjectId,
    bodyBindingId: preferredIdentity.bodyBindingId ?? current.bodyBindingId ?? candidate.bodyBindingId,
  }
}

/**
 * 单个 mesh 的解析结果：
 * - `bodyEntries`：mesh 级别去重后的全部实体列表，用于设计树展示
 * - `primitiveEntityMap`：`primitiveIndex → entity 条目`，由 `primitiveIndices` 字段建立，
 *   用于精确绑定每个 Three.js primitive 对应的实体
 */
export interface MeshBodyEntriesResult {
  bodyEntries: ImportedBodyEntry[]
  /** primitiveIndex → 该 primitive 对应的 STEP 实体条目 */
  primitiveEntityMap: Map<number, ImportedBodyEntry>
}

/**
 * 从 GLTF parser JSON 的单个 mesh 对象中提取几何 body 条目与 primitive 映射。
 *
 * 设计要点：
 * - 只读取 mesh 级别的 step.entities，不读取 primitive 级别自身的 extras.step
 * - 利用每个 entity 上的 `primitiveIndices` 字段建立反向映射：
 *   primitiveIndex → entity，供后续类型检查、选择高亮等精确绑定使用
 */
function extractParserMeshBodyEntries(meshValue: unknown): MeshBodyEntriesResult {
  const empty: MeshBodyEntriesResult = { bodyEntries: [], primitiveEntityMap: new Map() }
  const meshRecord = asRecord(meshValue)
  if (!meshRecord) {
    return empty
  }

  const extrasRecord = asRecord(meshRecord.extras)

  // 只采集 mesh 级别的 step 元数据
  const candidateStepRecords: Array<Record<string, unknown> | null> = [
    asRecord(meshRecord.step),
    asRecord(meshRecord.stepMetadata),
    asRecord(meshRecord.assetStep),
    asRecord(extrasRecord?.step),
    asRecord(extrasRecord?.stepMetadata),
    asRecord(extrasRecord?.assetStep),
  ]

  // 找到第一个包含 entities 的 step 记录
  const entityItems: unknown[] = []
  for (const stepRecord of candidateStepRecords) {
    if (Array.isArray(stepRecord?.entities) && stepRecord.entities.length > 0) {
      entityItems.push(...stepRecord.entities)
      break
    }
  }

  if (entityItems.length === 0) {
    return empty
  }

  // 构建 body 条目列表与 primitiveIndex 映射
  const primitiveEntityMap = new Map<number, ImportedBodyEntry>()
  const bodyEntries: ImportedBodyEntry[] = []
  const seenBodyIds = new Set<string>()

  entityItems.forEach((entityItem, fallbackIndex) => {
    const entry = normalizeImportedBodyEntry(entityItem, fallbackIndex + 1)
    if (!entry) {
      return
    }

    // 去重加入 bodyEntries
    const key = entry.bodyId.trim()
    if (key && !seenBodyIds.has(key)) {
      seenBodyIds.add(key)
      bodyEntries.push(entry)
    }

    // 按 primitiveIndices 建立反向映射
    const entityRecord = asRecord(entityItem)
    const primitiveIndices = Array.isArray(entityRecord?.primitiveIndices)
      ? (entityRecord.primitiveIndices as unknown[])
      : []
    primitiveIndices.forEach((idx) => {
      if (typeof idx === 'number' && Number.isInteger(idx) && idx >= 0) {
        // 同一 primitive 被多个 entity 共享时取第一个
        if (!primitiveEntityMap.has(idx)) {
          primitiveEntityMap.set(idx, entry)
        }
      }
    })
  })

  return { bodyEntries, primitiveEntityMap }
}

/**
 * 从 GLTF parser JSON 的 nodes 与 meshes 中构建双张映射表：
 * - `nodeBodyEntries`：`nodeIndex → body 条目列表`（设计树展示用）
 * - `nodePrimitiveEntityMap`：`nodeIndex → (primitiveIndex → entity)`（primitive 精确绑定用）
 *
 * 过滤规则：
 * - 节点 extras.step.objectType 为 'assembly' 时跳过，装配层级节点不产生 body 条目
 */
export function buildParserNodeBodyEntriesMap(parserJson: unknown): {
  nodeBodyEntries: Map<number, ImportedBodyEntry[]>
  nodePrimitiveEntityMap: Map<number, Map<number, ImportedBodyEntry>>
} {
  const parserRecord = asRecord(parserJson)
  const nodeBodyEntries = new Map<number, ImportedBodyEntry[]>()
  const nodePrimitiveEntityMap = new Map<number, Map<number, ImportedBodyEntry>>()
  const meshItems = Array.isArray(parserRecord?.meshes) ? parserRecord.meshes : []
  const nodes = Array.isArray(parserRecord?.nodes) ? parserRecord.nodes : []

  nodes.forEach((nodeValue, nodeIndex) => {
    const nodeRecord = asRecord(nodeValue)
    const meshIndex = typeof nodeRecord?.mesh === 'number' ? nodeRecord.mesh : null
    if (meshIndex === null || meshIndex < 0 || meshIndex >= meshItems.length) {
      return
    }

    // 仅通过 extras.step.objectType 判断装配节点，该字段由导出器在 node 级别明确写入
    const nodeExtrasRecord = asRecord(nodeRecord?.extras)
    const nodeStepRecord = asRecord(nodeExtrasRecord?.step)
    const nodeObjectType = readStringCandidate(nodeStepRecord?.objectType).toLowerCase()
    if (nodeObjectType === 'assembly') {
      return
    }

    const result = extractParserMeshBodyEntries(meshItems[meshIndex])
    if (result.bodyEntries.length === 0) {
      return
    }
    nodeBodyEntries.set(nodeIndex, result.bodyEntries)
    if (result.primitiveEntityMap.size > 0) {
      nodePrimitiveEntityMap.set(nodeIndex, result.primitiveEntityMap)
    }
  })

  return { nodeBodyEntries, nodePrimitiveEntityMap }
}
